import { useCallback, useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { payScheduleQueryKeys } from '@xemplo/gp-constants';
import {
  useCreateTagMutation,
  useTagsListQuery,
  useUpdateEntityTagsMutation,
} from '@xemplo/gp-tags-api';
import { PersonaRole } from '@xemplo/gp-types';
import { useGetPayscheduleByIdQuery } from '@xemplo/pay-schedule-query';
import { usePromptDialog } from '@xemplo/prompt-dialog';
import { useRoleAuthorisation } from '@xemplo/roles-query';
import { safeCallAsync } from '@xemplo/safe-call';
import { createNoResultList, ListBuilder } from '@xemplo/stateful-list-core';
import { useServerSideHelper } from '@xemplo/tag-field';

import { TagsFormData } from './edit-tags-prompt.types';
interface UseTagsFieldProps {
  promptDialog: ReturnType<typeof usePromptDialog>;
  payscheduleId?: string;
}

/**
 * Consolidated hook to manage all tag field operations.
 */
export function useTagsField({ promptDialog, payscheduleId }: UseTagsFieldProps) {
  const { payscheduleId: urlParam } = useParams<{ payscheduleId: string }>();
  const effectivePayscheduleId = payscheduleId ?? urlParam;

  // Server-side search handler
  const { search, debouncedSearch, beforeTagCreate, onOpenChange } =
    useServerSideHelper();

  //Fetch all required tag data
  const { existingTags, tagsList, payrollServiceProviderId } = useTagFieldData(
    search,
    effectivePayscheduleId,
  );

  // Initialize form with existing tags
  const methods = useForm<TagsFormData>({
    defaultValues: {
      tags: existingTags || [],
    },
  });

  //Tag creation/updating
  const { handleConfirm, handleOnTagCreate, isUpdating } = useTagFieldMutations({
    beforeTagCreate,
    methods,
    promptDialog,
    payrollServiceProviderId,
    payscheduleId: effectivePayscheduleId,
  });

  const { reset } = methods;

  useEffect(() => {
    reset({ tags: existingTags || [] });
  }, [existingTags, promptDialog.isOpen, reset]);

  return {
    methods,
    tagsList,
    handleOnTagCreate,
    handleConfirm,
    isUpdating,
    debouncedSearch,
    onOpenChange,
  };
}

/**
 * Hook responsible for fetching all required data for the pay schedule tag field
 * Converts tags response into a stateful list required for the TagField component
 */
const useTagFieldData = (search: string, payscheduleId: string | undefined) => {
  const { data: payScheduleData, isLoading: payScheduleDataLoading } =
    useGetPayscheduleByIdQuery({
      id: payscheduleId,
    });

  //If the logged in user is SA we need to include the PSP ID in the request
  const { isInRole, isLoading: roleLoading } = useRoleAuthorisation();
  const isSa = isInRole([PersonaRole.SystemAdmin]);
  const payrollServiceProviderId = isSa
    ? payScheduleData?.result.payrollServiceProviderId
    : undefined;

  const isLoading = payScheduleDataLoading || roleLoading;

  // Fetch tags with server-side search
  const { data: tagsResponse } = useTagsListQuery(
    {
      filter: {
        value: search,
        payrollServiceProviderId,
      },
    },
    !isLoading,
  );

  const existingTags = payScheduleData?.result.tags;
  const searchTags = tagsResponse?.result?.rows;
  // Transform tags to stateful list (uses values as both ID and label, tag values are unique)
  const tagsList = useMemo(() => {
    if (!searchTags || searchTags.length === 0) {
      return createNoResultList({ label: 'No Tags Found' });
    }

    return ListBuilder.create()
      .addGroup((group) => {
        searchTags.forEach((tag) => group.addItem(tag.value, tag.value));
        return group;
      })
      .build();
  }, [searchTags]);
  return {
    existingTags,
    searchTags,
    tagsList,
    payrollServiceProviderId,
  };
};

/**
 * Hook for handling tag creation and updating
 * @handleOnTagCreate handles creating a new tag and adding it to the tag list
 * @handleConfirm handles saving the new tag list to the entity (in this case the pay schedule)
 */
const useTagFieldMutations = (props: {
  methods: UseFormReturn<TagsFormData>;
  beforeTagCreate: () => void;
  promptDialog: ReturnType<typeof usePromptDialog>;
  payrollServiceProviderId: string | undefined;
  payscheduleId: string | undefined;
}) => {
  const {
    methods,
    beforeTagCreate,
    promptDialog,
    payrollServiceProviderId,
    payscheduleId,
  } = props;
  const { getValues, setValue } = methods;
  const queryClient = useQueryClient();
  const { mutateAsync: createTagAsync } = useCreateTagMutation();

  const { mutateAsync: updateEntityTagsAsync, isPending: isUpdating } =
    useUpdateEntityTagsMutation({
      onSuccess: () => {
        promptDialog.closeDialog();
        queryClient.invalidateQueries({
          queryKey: payScheduleQueryKeys.details(),
        });
      },
    });

  // Handle tag creation
  const handleOnTagCreate = useCallback(
    async (value: string) => {
      beforeTagCreate();

      const [response] = await safeCallAsync(
        createTagAsync({ value, payrollServiceProviderId }),
      );

      if (!response) return;

      // Store the tag value directly in the form
      const currentTags = getValues('tags') || [];
      setValue('tags', [value, ...currentTags]);
    },
    [beforeTagCreate, createTagAsync, getValues, payrollServiceProviderId, setValue],
  );

  // Handle tags save to payschedule
  const handleConfirm = methods.handleSubmit((data) => {
    if (!payscheduleId) return;

    // Tags are already stored as values, cast to strings
    const tagValues = data.tags as string[];
    return safeCallAsync(
      updateEntityTagsAsync({
        entityType: 'PaySchedule',
        entityId: payscheduleId,
        tags: tagValues,
      }),
    );
  });

  return {
    handleConfirm,
    handleOnTagCreate,
    isUpdating,
  };
};
